import { MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlayerStore } from "@/lib/player/player.store";
import { RegisterPromptDialog } from "@/components/registerPromptDialog.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalChat } from "@/features/chat/components/globalChat.component";
import { toast } from "sonner";
import { PodcastService } from "@/lib/podcast/podcast.service";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";

export function FloatingChatButton() {
	const { isAuthenticated } = useAuth();
	const { t } = useTranslation();
	const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
	const [isChatOpen, setIsChatOpen] = useState(false);
	const podWorkspaceId = usePlayerStore((state) => state.podWorkspaceId);
	const podcastId = usePlayerStore((state) => state.podcastId);
	const currentMedia = usePlayerStore((state) => state.currentMedia);

	// Fetch embedding status
	const { data: podcast } = useQuery({
		queryKey: ["podcast", podcastId],
		queryFn: () => PodcastService.getPodcastById(podcastId as string),
		enabled: !!podcastId && !!podWorkspaceId,
		// Poll every 5 seconds when embedding is processing
		refetchInterval: (query) => {
			const embeddingStatus = query.state.data?.embeddingStatus;
			const isProcessing =
				embeddingStatus?.status === "PROCESSING" ||
				embeddingStatus?.status === "PENDING";
			return isProcessing ? 5000 : false; // Poll only when processing
		},
		// Don't show stale data while refetching
		staleTime: 0,
		// Automatically pause polling when tab is inactive
		refetchIntervalInBackground: false,
	});

	const embeddingStatus = podcast?.embeddingStatus || null;

	const isEmbeddingProcessing =
		embeddingStatus?.status === "PROCESSING" ||
		embeddingStatus?.status === "PENDING";
	const isEmbeddingFailed = embeddingStatus?.status === "FAILED";
	const isChatDisabled = isEmbeddingProcessing || isEmbeddingFailed;

	const handleChatClick = () => {
		if (!isAuthenticated) {
			setShowRegisterPrompt(true);
			return;
		}

		// Check if embeddings are still processing for current workspace
		if (podWorkspaceId && isEmbeddingProcessing) {
			toast.info(t("chat.embeddingProcessing"), {
				description: t("chat.embeddingProcessingDescription"),
			});
			return;
		}

		// Check if embeddings failed for current workspace
		if (podWorkspaceId && isEmbeddingFailed) {
			toast.error(t("chat.embeddingFailed"), {
				description: t("chat.embeddingFailedDescription"),
			});
			return;
		}

		// Always open chat - if no workspace, it will show selection UI
		setIsChatOpen(true);
	};

	// Habit Loop v1 - allow other surfaces (e.g. the post-listen prompt) to open
	// the global chat via a window event.
	useEffect(() => {
		const openHandler = () => handleChatClick();
		window.addEventListener("podhive:open-global-chat", openHandler);
		return () => {
			window.removeEventListener("podhive:open-global-chat", openHandler);
		};
		// handleChatClick is recreated each render; we intentionally bind the latest
		// via closure on each mount-stable listener swap.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isAuthenticated,
		isEmbeddingProcessing,
		isEmbeddingFailed,
		podWorkspaceId,
	]);

	// Determine tooltip content
	const getTooltipContent = () => {
		if (!podWorkspaceId) {
			return t("chat.openChat");
		}
		if (isEmbeddingProcessing) {
			return t("chat.embeddingProcessingTooltip");
		}
		if (isEmbeddingFailed) {
			return t("chat.embeddingFailedTooltip");
		}
		return t("chat.openChat");
	};

	return (
		<>
			{/* Floating chat button - bottom center when no player, bottom right above player on desktop only */}
			<div className="hidden md:block fixed z-40 transition-all duration-300 bottom-32 right-8">
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="iconLg"
								onClick={handleChatClick}
								disabled={isChatDisabled && !!podWorkspaceId}
								className="h-14 w-14 rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(249,115,22,0.4)] transition-all duration-300 hover:scale-110 active:scale-95 bg-gradient-to-br from-orange-400/80 via-orange-500/70 to-orange-600/80 hover:from-orange-400/90 hover:via-orange-500/80 hover:to-orange-600/90 backdrop-blur-md border border-white/20 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/30 before:via-white/10 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300 after:absolute after:inset-[1px] after:rounded-full after:bg-gradient-to-br after:from-transparent after:via-transparent after:to-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label={getTooltipContent()}
							>
								{isEmbeddingProcessing && podWorkspaceId ? (
									<>
										<Loader2 className="h-7 w-7 relative z-10 drop-shadow-lg animate-spin" />
										{/* Processing badge */}
										<span className="absolute -top-1 -right-1 h-4 w-4 bg-yellow-500 rounded-full border-2 border-white animate-pulse" />
									</>
								) : isEmbeddingFailed && podWorkspaceId ? (
									<>
										<MessageCircle className="h-7 w-7 relative z-10 drop-shadow-lg" />
										{/* Failed badge */}
										<span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full border-2 border-white" />
									</>
								) : (
									<MessageCircle className="h-7 w-7 relative z-10 drop-shadow-lg animate-float-icon" />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="left" className="max-w-xs">
							<div className="space-y-1">
								<p className="font-semibold">{getTooltipContent()}</p>
								{isEmbeddingProcessing && embeddingStatus && (
									<p className="text-xs text-muted-foreground">
										{t("chat.embeddingProgress", {
											completed: embeddingStatus.completedChapters,
											total: embeddingStatus.totalChapters,
										})}
									</p>
								)}
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>

			{/* Register Prompt Dialog */}
			<RegisterPromptDialog
				open={showRegisterPrompt}
				onOpenChange={setShowRegisterPrompt}
				title={t("chat.registerPrompt.title")}
				description={t("chat.registerPrompt.description")}
				customMessage={t("chat.registerPrompt.message")}
			/>

			{/* Global Chat - always available, shows workspace selection if no active podcast */}
			{isChatOpen && (
				<GlobalChat
					defaultOpen={isChatOpen}
					onOpenChange={setIsChatOpen}
					podWorkspaceId={podWorkspaceId || undefined}
					workspaceTitle={currentMedia?.subtitle}
					hideTrigger={true}
				/>
			)}
		</>
	);
}
